import React, { useState, useEffect } from 'react';
import { featureDesignStudioApi } from '../services/featureDesignStudioApi';
import RequirementInput, { SESSION_STORAGE_KEY } from '../components/FeatureDesignStudio/RequirementInput';
import DesignBoard from '../components/FeatureDesignStudio/DesignBoard';
import TaskDetailDrawer from '../components/FeatureDesignStudio/TaskDetailDrawer';
import BuildTasksModal from '../components/FeatureDesignStudio/BuildTasksModal';
import './FeatureDesignStudio.css';

export default function FeatureDesignStudio() {
  const [draft, setDraft] = useState(null);
  const [layout, setLayout] = useState({});
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTaskTempId, setSelectedTaskTempId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [buildModalOpen, setBuildModalOpen] = useState(false);

  const tasks = draft?.tasks || [];

  useEffect(() => {
    if (draft?.tasks?.length && layout) {
      try {
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ draft, layout }));
      } catch (e) {
        // ignore quota or disabled storage
      }
    }
  }, [draft, layout]);

  const handleGenerate = async (requirementText, featureTitle) => {
    setGenerating(true);
    setError(null);
    try {
      const result = await featureDesignStudioApi.generate({
        requirement_text: requirementText,
        feature_title: featureTitle || undefined
      });
      setDraft({
        feature_title: result.feature_title,
        summary: result.summary,
        assumptions: result.assumptions || [],
        out_of_scope: result.out_of_scope || [],
        tasks: result.tasks || []
      });
      setLayout({});
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleRefine = async (feedback, taskTempId) => {
    if (!draft?.tasks?.length) return;
    setError(null);
    const result = await featureDesignStudioApi.refine({
      draft,
      user_instruction: feedback,
      task_temp_id: taskTempId || undefined
    });
    setDraft((prev) => ({
      ...prev,
      feature_title: result.feature_title ?? prev.feature_title,
      summary: result.summary ?? prev.summary,
      assumptions: result.assumptions ?? prev.assumptions,
      out_of_scope: result.out_of_scope ?? prev.out_of_scope,
      tasks: result.tasks || []
    }));
  };

  const handleUpdateTask = (tempId, updates) => {
    setDraft((prev) => ({
      ...prev,
      tasks: (prev.tasks || []).map((t) =>
        t.temp_id === tempId || t.id === tempId ? { ...t, ...updates } : t
      )
    }));
  };

  const handleRestoreLast = (restoredDraft, restoredLayout) => {
    setDraft(restoredDraft);
    setLayout(restoredLayout || {});
    setError(null);
  };

  const selectedTask = selectedTaskTempId
    ? tasks.find((t) => t.temp_id === selectedTaskTempId || t.id === selectedTaskTempId)
    : null;

  return (
    <div className="feature-design-studio">
      <div className="fds-header">
        <h1>Feature Design Studio</h1>
        <p className="fds-subtitle">
          Design-time task plans from requirements. Tasks are created only when you click Build
          Tasks.
        </p>
      </div>

      {error && (
        <div className="alert alert-danger fds-error" role="alert">
          {error}
        </div>
      )}

      <div className="fds-main">
        <div className="fds-left">
          <RequirementInput
            onGenerate={handleGenerate}
            generating={generating}
            hasDraft={!!draft?.tasks?.length}
            onRestoreLast={handleRestoreLast}
          />
        </div>

        <div className="fds-board-area">
          {draft?.tasks?.length ? (
            <>
              <div className="fds-board-toolbar">
                <span className="fds-session-title">{draft.feature_title || 'Untitled'}</span>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() => setBuildModalOpen(true)}
                >
                  Build Tasks
                </button>
              </div>
              <DesignBoard
                tasks={tasks}
                layout={layout}
                onLayoutChange={setLayout}
                onTasksChange={(newTasks) =>
                  setDraft((prev) => (prev ? { ...prev, tasks: newTasks } : null))
                }
                onSelectTask={setSelectedTaskTempId}
                selectedTaskTempId={selectedTaskTempId}
                onOpenDrawer={() => setDrawerOpen(true)}
              />
            </>
          ) : (
            <div className="fds-empty">
              <p>
                Enter a feature requirement and click Build Tasks to generate a task plan. Edit on
                the board, then use Build Tasks to create real tasks (managers only).
              </p>
            </div>
          )}
        </div>

        {drawerOpen && selectedTask && (
          <TaskDetailDrawer
            task={selectedTask}
            onClose={() => {
              setDrawerOpen(false);
              setSelectedTaskTempId(null);
            }}
            onSave={(updates) =>
              handleUpdateTask(selectedTask.temp_id || selectedTask.id, updates)
            }
            onRefine={handleRefine}
          />
        )}

        {buildModalOpen && draft && (
          <BuildTasksModal
            draft={draft}
            onClose={() => setBuildModalOpen(false)}
            onSuccess={() => setBuildModalOpen(false)}
          />
        )}
      </div>
    </div>
  );
}
